import { Controller } from '@hotwired/stimulus'
import { confirmDiscard } from '~/dirty_forms'

// UX S3 form modal (was the F4 side panel): a native modal <dialog> (focus trap, Esc) around <turbo-frame id="modal">. Content arriving in the
// frame opens it; the server emptying the frame (save) or the user closing it closes it, clears the frame and returns
// focus to the link that opened it. Closing by hand (×, Zrušit, backdrop, Esc) asks first about unsaved changes.
const FIRST_FIELD = 'input:enabled:not([type=hidden]), select:enabled, textarea:enabled'

export default class ModalController extends Controller<HTMLDialogElement> {
  static override targets = ['frame']
  declare readonly frameTarget: HTMLElement
  private opener: HTMLElement | null = null
  // The opener may be redrawn by the save (a Tabule tile is replaced): its id finds the new copy; when the row left
  // the tile (status filter), focus goes to the redrawn tile's first link (its title).
  private openerId = ''
  private openerTileId = ''
  private observer: MutationObserver | null = null

  private readonly rememberOpener = (event: Event): void => {
    const link = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-turbo-frame="modal"]') : null
    if (link) {
      this.opener = link
      this.openerId = link.id
      this.openerTileId = link.closest('[data-board-tile]')?.id ?? ''
    }
  }

  // Turbo caches the page for Back/Forward: never with an open (or half-open) panel.
  private readonly reset = (): void => {
    this.clear()
    if (this.element.open) this.element.close()
  }

  override connect(): void {
    this.reset() // a restored snapshot may still hold the last panel
    this.observer = new MutationObserver(() => this.sync())
    this.observer.observe(this.frameTarget, { childList: true })
    document.addEventListener('click', this.rememberOpener, true)
    document.addEventListener('turbo:before-cache', this.reset)
  }

  override disconnect(): void {
    this.observer?.disconnect()
    document.removeEventListener('click', this.rememberOpener, true)
    document.removeEventListener('turbo:before-cache', this.reset)
  }

  // ×, Zrušit, backdrop.
  close(): void {
    if (this.element.open && confirmDiscard(this.frameTarget)) this.element.close()
  }

  // Esc (dialog "cancel").
  cancel(event: Event): void {
    if (!confirmDiscard(this.frameTarget)) event.preventDefault()
  }

  // dialog "close" (any way it closed).
  closed(): void {
    this.clear()
    const opener = this.opener?.isConnected ? this.opener : this.redrawnOpener()
    opener?.focus()
    this.opener = null
    this.openerId = ''
    this.openerTileId = ''
  }

  private redrawnOpener(): HTMLElement | null {
    const byId = this.openerId ? document.getElementById(this.openerId) : null
    // I-UXC-board-2: the redrawn row may sit in a folded tile section; open it so focus lands on a visible row.
    byId?.closest('details')?.setAttribute('open', '')
    if (byId) return byId
    const tile = this.openerTileId ? document.getElementById(this.openerTileId) : null
    return tile?.querySelector<HTMLElement>('a[href]') ?? null
  }

  private sync(): void {
    const filled = this.frameTarget.childElementCount > 0
    if (filled) {
      // P14/I-CALM-04: the first field, not the × that showModal() picks (returned to the opener on close, see closed()).
      // New content while open (the next blank form of „Uložit a přidat další“, a 422) took the old focus with it.
      const opening = !this.element.open
      if (opening) this.element.showModal()
      if (opening || !this.frameTarget.contains(document.activeElement)) this.frameTarget.querySelector<HTMLElement>(FIRST_FIELD)?.focus()
    }
    else if (!filled && this.element.open) this.element.close()
  }

  private clear(): void {
    this.frameTarget.removeAttribute('src')
    if (this.frameTarget.childElementCount > 0) this.frameTarget.replaceChildren()
  }
}
